import CaregiverExtendedContactDetails from "../../caregiver/components/CaregiverExtendedContactDetails";
import CaregiverProfileComplianceAutomationsSection from "../sections/compliance/automations/CaregiverProfileComplianceAutomationsSection";
import useProfileWindowSettings from "../../profileWindowSettings/useProfileWindowSettings";
import React from "react";
import { Box, Text } from "@chakra-ui/react";
import EntityProfileSectionCard from "../../entityProfileModal/components/EntityProfileSectionCard";
import { EntityProfileLoadingState } from "../../entityProfileModal/components/EntityProfileFeedback";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../entityProfileModal/entityProfileModal.theme";
import type { CaregiverProfileBridgeActions } from "../caregiverProfileModal.actions";
import { CaregiverProfileSection } from "../caregiverProfileModal.sections";
import type { CaregiverProfileCaregiver } from "../caregiverProfileModal.types";
import { CaregiverId } from "../../../shared/schema/schema";
import CaregiverProfileRecentActivitySection from "../sections/administrative/recentActivity/CaregiverProfileRecentActivitySection";
import CaregiverProfileHoursOverridesSection from "../sections/administrative/hoursOverrides/CaregiverProfileHoursOverridesSection";
import CaregiverProfilePayRatesSection from "../sections/administrative/payRates/CaregiverProfilePayRatesSection";
import CaregiverProfilePayrollTaxSection from "../sections/administrative/payrollTax/CaregiverProfilePayrollTaxSection";
import CaregiverProfileAssignedTasksSection from "../sections/calendar/assignedTasks/CaregiverProfileAssignedTasksSection";
import CaregiverProfileAttendanceSection from "../sections/calendar/attendance/CaregiverProfileAttendanceSection";
import CaregiverProfileAvailabilitySection from "../sections/calendar/availability/CaregiverProfileAvailabilitySection";
import CaregiverProfileCalendarSection from "../sections/calendar/visits/CaregiverProfileCalendarSection";
import CaregiverProfilePatientsSection from "../sections/calendar/patients/CaregiverProfilePatientsSection";
import CaregiverProfileVisitsBroadcastedSection from "../sections/calendar/visitsBroadcasted/CaregiverProfileVisitsBroadcastedSection";
import CaregiverProfileApplicationSection from "../sections/compliance/application/CaregiverProfileApplicationSection";
import CaregiverProfileComplianceSection from "../sections/compliance/compliance/CaregiverProfileComplianceSection";
import CaregiverProfileTrainingCenterSection from "../sections/compliance/trainingCenter/CaregiverProfileTrainingCenterSection";
import CaregiverProfileDocumentsSection from "../sections/documents/documents/CaregiverProfileDocumentsSection";
import CaregiverProfileFaxesSection from "../sections/documents/faxes/CaregiverProfileFaxesSection";
import CaregiverProfileDirectDepositSection from "../sections/payroll/directDeposit/CaregiverProfileDirectDepositSection";
import CaregiverProfilePayrollSection from "../sections/payroll/payroll/CaregiverProfilePayrollSection";
import CaregiverProfileAboutSection from "../sections/profile/about/CaregiverProfileAboutSection";
import CaregiverProfileEmergencyContactSection from "../sections/profile/emergencyContact/CaregiverProfileEmergencyContactSection";
import CaregiverProfilePatientRelationshipsSection from "../sections/profile/relationships/CaregiverProfilePatientRelationshipsSection";
import CaregiverProfileStaffingPreferencesSection from "../sections/profile/staffingPreferences/CaregiverProfileStaffingPreferencesSection";
import CaregiverProfileStatusReviewSection from "../sections/profile/statusReview/CaregiverProfileStatusReviewSection";
import CaregiverProfileSuppliesShipmentsSection from "../sections/profile/suppliesShipments/CaregiverProfileSuppliesShipmentsSection";
import CaregiverProfileValidatedDutiesSection from "../sections/profile/validatedDuties/CaregiverProfileValidatedDutiesSection";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
  isProfileLoading: boolean;
  section: CaregiverProfileSection;
};

const profileBackedSubsectionIds = new Set([
  "about",
  "emergency-contact",
  "supplies-shipments",
  "training-center",
]);

function PlaceholderSection() {
  return (
    <Box
      bg={entityProfileModalColors.surfaceMuted}
      border={entityProfileModalBorders.panel}
      borderRadius="2px"
      px={4}
      py={5}
    >
      <Text color={entityProfileModalColors.muted}>Pending</Text>
    </Box>
  );
}

export default function CaregiverProfileSections(props: Props) {
  const [{ saveCalendarSelection }] = useProfileWindowSettings();
  const calendarCards = React.useRef<Props["section"]["subsections"]>([]);
  if (!saveCalendarSelection) {
    calendarCards.current = [];
  } else if (props.section.label === "Calendar") {
    calendarCards.current = props.section.subsections.filter(
      (subsection) => subsection.id === "calendar" || subsection.id === "list-view"
    );
  }
  const subsections =
    props.section.label === "Calendar"
      ? props.section.subsections
      : [...calendarCards.current, ...props.section.subsections];
  const getSectionTitle = (subsectionId: string, fallback: string) => {
    if (subsectionId === "availability") {
      return "Availability Preferences";
    }

    return fallback;
  };

  const isCalendarCardHidden = (subsection: Props["section"]["subsections"][number]) =>
    props.section.label !== "Calendar" && calendarCards.current.includes(subsection);

  return (
    <>
      {subsections.map((subsection) => (
        <EntityProfileSectionCard
          key={subsection.id}
          entity="caregiver"
          id={subsection.id}
          idPrefix="react-caregiver-section"
          isHidden={isCalendarCardHidden(subsection)}
          title={getSectionTitle(subsection.id, subsection.label)}
        >
          {props.isProfileLoading && profileBackedSubsectionIds.has(subsection.id) ? (
            <EntityProfileLoadingState label="Loading profile details..." />
          ) : subsection.id === "calendar" ? (
            <CaregiverProfileCalendarSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
              isHidden={isCalendarCardHidden(subsection)}
              view="calendar"
            />
          ) : subsection.id === "list-view" ? (
            <CaregiverProfileCalendarSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
              isHidden={isCalendarCardHidden(subsection)}
              view="list"
            />
          ) : subsection.id === "assigned-tasks" ? (
            <CaregiverProfileAssignedTasksSection
              actions={props.actions}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "availability" ? (
            <CaregiverProfileAvailabilitySection caregiverId={props.caregiverId} />
          ) : subsection.id === "attendance" ? (
            <CaregiverProfileAttendanceSection caregiverId={props.caregiverId} />
          ) : subsection.id === "visits-broadcasted" ? (
            <CaregiverProfileVisitsBroadcastedSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "patient-list" ? (
            <CaregiverProfilePatientsSection
              actions={props.actions}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "compliance" ? (
            <CaregiverProfileComplianceSection
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "compliance-automations" ? (
            <CaregiverProfileComplianceAutomationsSection caregiverId={props.caregiverId} />
          ) : subsection.id === "application" ? (
            <CaregiverProfileApplicationSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "training-center" ? (
            props.caregiver === undefined ? (
              <EntityProfileLoadingState label="Loading profile details..." />
            ) : (
              <CaregiverProfileTrainingCenterSection caregiver={props.caregiver} />
            )
          ) : subsection.id === "about" ? (
            props.caregiver === undefined ? (
              <EntityProfileLoadingState label="Loading profile details..." />
            ) : (
              <CaregiverProfileAboutSection actions={props.actions} caregiver={props.caregiver} />
            )
          ) : subsection.id === "status-review" ? (
            <CaregiverProfileStatusReviewSection caregiverId={props.caregiverId} />
          ) : subsection.id === "staffing-preferences" ? (
            <CaregiverProfileStaffingPreferencesSection caregiverId={props.caregiverId} />
          ) : subsection.id === "validated-duties" ? (
            <CaregiverProfileValidatedDutiesSection
              caregiverId={props.caregiverId}
              officeIds={props.isProfileLoading ? undefined : props.caregiver?.officeIds}
            />
          ) : subsection.id === "extended-contact-details" ? (
            <CaregiverExtendedContactDetails caregiverId={props.caregiverId} />
          ) : subsection.id === "emergency-contact" ? (
            props.caregiver === undefined ? (
              <EntityProfileLoadingState label="Loading profile details..." />
            ) : (
              <CaregiverProfileEmergencyContactSection caregiver={props.caregiver} />
            )
          ) : subsection.id === "supplies-shipments" ? (
            props.caregiver === undefined ? (
              <EntityProfileLoadingState label="Loading profile details..." />
            ) : (
              <CaregiverProfileSuppliesShipmentsSection caregiver={props.caregiver} />
            )
          ) : subsection.id === "patient-caregiver-relationships" ? (
            <CaregiverProfilePatientRelationshipsSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "documents" ? (
            <CaregiverProfileDocumentsSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "faxes" ? (
            <CaregiverProfileFaxesSection caregiverId={props.caregiverId} />
          ) : subsection.id === "recent-activity" ? (
            <CaregiverProfileRecentActivitySection caregiverId={props.caregiverId} />
          ) : subsection.id === "pay-rates" ? (
            <CaregiverProfilePayRatesSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "payroll-tax-information" ? (
            <CaregiverProfilePayrollTaxSection caregiverId={props.caregiverId} />
          ) : subsection.id === "hours-overrides" ? (
            <CaregiverProfileHoursOverridesSection
              actions={props.actions}
              caregiver={props.isProfileLoading ? undefined : props.caregiver}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "payroll" ? (
            <CaregiverProfilePayrollSection
              actions={props.actions}
              caregiverId={props.caregiverId}
            />
          ) : subsection.id === "direct-deposit-information" ? (
            <CaregiverProfileDirectDepositSection caregiverId={props.caregiverId} />
          ) : (
            <PlaceholderSection />
          )}
        </EntityProfileSectionCard>
      ))}
    </>
  );
}
